import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Pause, Play, Salad, Tag, X } from 'lucide-react';
import { ernaehrungAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';

/* Zusatz-Abo „Ernährungscoaching“ (E0, Entscheidung des Betreibers 15.10.2026): Karte auf Ernährung › Mitglied.
   Preis, Laufzeiten und Kündigungstermin rechnet der Server (ernaehrung/abo.py) — hier wird nichts gerechnet.
   Das Abo ist eine Position auf der bestehenden Monatsrechnung, eingezogen mit dem vorhandenen SEPA-Mandat. */

const euro = (v) => `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');
const heuteIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const ZUSTAND = {
  aktiv: ['aktiv', 'bg-emerald-500/10 text-emerald-700 border-emerald-200'],
  pausiert: ['pausiert', 'bg-amber-500/10 text-amber-700 border-amber-200'],
  wartet_zahlung: ['wartet auf Zahlung', 'bg-sky-500/10 text-sky-700 border-sky-200'],     // E23: Stripe-Link verschickt
  beendet: ['beendet', 'bg-muted text-muted-foreground'],
  keins: ['kein Abo', 'bg-muted text-muted-foreground'],
};

function MitGrund({ text, knopf, onOk, onAbbruch, mitTermin, mindestens }) {
  const [grund, setGrund] = useState('');
  const [zum, setZum] = useState('');
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
      <p className="text-sm">{text}</p>
      <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Grund (optional)" maxLength={300} aria-label="Grund" />
      {mitTermin && (
        <FormField label="Zum Wunschtermin (leer = frühestmöglich)" hint={mindestens ? `Frühestens zum ${tag(mindestens)}, immer zum Monatsende.` : 'Immer zum Monatsende.'}>
          <Input type="date" value={zum} onChange={(e) => setZum(e.target.value)} className="w-44" />
        </FormField>
      )}
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" onClick={() => onOk(grund.trim(), zum || null)}>{knopf}</Button>
        <Button size="sm" variant="ghost" onClick={onAbbruch}>Abbrechen</Button>
      </div>
    </div>
  );
}

function TagsZeile({ leadId, tags: start, darf }) {
  const [tags, setTags] = useState(start || []);
  const [neu, setNeu] = useState('');
  useEffect(() => { setTags(start || []); }, [start]);
  const speichern = async (liste) => {
    try { const { data } = await ernaehrungAPI.tagsSetzen(leadId, liste); setTags(data.tags); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium flex items-center gap-1"><Tag className="w-3 h-3" /> Tags <span className="text-muted-foreground font-normal">(für Segmente: Kochbücher, Automatik)</span></p>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <Badge key={t} variant="outline" className="gap-1">{t}
            {darf && <button type="button" aria-label={`${t} entfernen`} onClick={() => speichern(tags.filter((x) => x !== t))}><X className="w-3 h-3" /></button>}
          </Badge>
        ))}
        {!tags.length && <span className="text-xs text-muted-foreground">keine</span>}
      </div>
      {darf && (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (neu.trim()) { speichern([...tags, neu.trim()]); setNeu(''); } }}>
          <Input value={neu} onChange={(e) => setNeu(e.target.value)} placeholder="Tag hinzufügen, z. B. fettabbau" maxLength={40} className="h-8 text-sm" />
          <Button type="submit" size="sm" variant="outline" className="h-8">Hinzufügen</Button>
        </form>
      )}
    </div>
  );
}

export function ZusatzAboKarte({ leadId, abo, produkt, stand, onNeu }) {
  const navigate = useNavigate();
  const [darf, setDarf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [neu, setNeu] = useState(null);           // { start, preis_monat }
  const [frage, setFrage] = useState(null);       // kuendigen | pausieren
  useEffect(() => { ernaehrungAPI.aboProdukt().then((r) => setDarf(!!r.data.darf_aendern)).catch(() => setDarf(false)); }, []);
  const z = abo?.zustand || 'keins';
  const [text, cls] = ZUSTAND[z] || [z, ''];
  const laeuft = abo && ['aktiv', 'pausiert', 'wartet_zahlung'].includes(z);

  const tu = async (aufruf, ok) => {
    setBusy(true);
    try { await aufruf(); if (ok) toast.success(ok); setNeu(null); setFrage(null); onNeu?.(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="zusatz-abo">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Salad className="w-4 h-4" /> Ernährungs-Abo
          <Badge variant="outline" className={cls}>{text}{abo?.gekuendigt && z !== 'beendet' ? ` · gekündigt zum ${tag(abo.ende)}` : ''}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {abo && z !== 'keins' && (
          <ul className="space-y-1">
            <li>{abo.produkt} · {euro(abo.preis_monat)} je Monat</li>
            <li>Start {tag(abo.start)} · Mindestlaufzeit bis {tag(abo.mindestlaufzeit_bis)}</li>
            {abo.naechste_faelligkeit && <li>Nächste Rechnung: Monat ab {tag(abo.naechste_faelligkeit)}</li>}
            {abo.ende && <li>Ende: {tag(abo.ende)}</li>}
            {abo.coach_name && <li>Coach: {abo.coach_name}</li>}
            {abo.startgebuehr > 0 && <li>Startgebühr {euro(abo.startgebuehr)} am {tag(abo.startgebuehr_am)}{abo.startgebuehr_abgerechnet ? ' (abgerechnet)' : ''}</li>}
            {abo.zahlweg === 'stripe' && <li>Zahlweg: Stripe-Abo zum {abo.zahltag}.{abo.stripe?.zahlung_fehlgeschlagen ? <span className="text-destructive"> · Zahlung fehlgeschlagen ({tag(abo.stripe.zahlung_fehlgeschlagen.am)})</span> : ''}</li>}
            {z === 'wartet_zahlung' && abo.stripe?.link && <li className="break-all text-xs">Zahlungslink: <a className="underline" href={abo.stripe.link} target="_blank" rel="noopener noreferrer">{abo.stripe.link}</a></li>}
            {z === 'pausiert' && abo.pause?.von && <li className="text-muted-foreground">Pause seit {tag(abo.pause.von)}{abo.pause.grund ? ` · ${abo.pause.grund}` : ''}</li>}
          </ul>
        )}
        {stand?.quelle === 'coaching' && stand.aktiv && <p className="text-xs text-muted-foreground">Freigeschaltet über eine Wochen-Buchung aus dem Shop (siehe Karte Coaching).</p>}
        <p className="text-xs text-muted-foreground">
          {laeuft
            ? 'Läuft als eigene Position auf der Monatsrechnung, eingezogen mit dem vorhandenen SEPA-Mandat. Kündigung: Frist zum Monatsende, nie vor Ende der Mindestlaufzeit.'
            : `Startwerte: ${euro(produkt?.preis_monat)} je Monat, Mindestlaufzeit ${produkt?.mindestlaufzeit_monate ?? 3} Monate, danach Frist ${produkt?.kuendigungsfrist_monate ?? 1} Monat zum Monatsende. Ohne Abo zeigt die App den Essen-Bereich nicht.`}
        </p>
        {/* E23 (15.10.2026): „Neuer Kunde in einem Fluss“ — Details · Vorlagen · Mitgliedschaft, Vorschau, Freigabe durch die Leitung.
            Steht allen offen, die die Karte sehen; der Coach bereitet vor, die Leitung bestätigt. */}
        {!laeuft && !neu && (
          <Button size="sm" variant={darf ? 'outline' : 'default'} onClick={() => navigate(`/ernaehrung/abschluss?lead_id=${leadId}`)} data-testid="abo-abschluss-fluss">Abo abschließen (in einem Fluss)</Button>
        )}
        {darf && (
          <div className="flex flex-wrap gap-2">
            {!laeuft && !neu && <Button size="sm" variant="ghost" onClick={() => setNeu({ start: heuteIso(), preis_monat: produkt?.preis_monat ?? 49, variante: 'standard' })}>Direkt anlegen (ohne Fluss)</Button>}
            {laeuft && !abo.gekuendigt && !frage && <Button size="sm" variant="outline" onClick={() => setFrage('kuendigen')}>Kündigen</Button>}
            {laeuft && z === 'aktiv' && !frage && <Button size="sm" variant="ghost" onClick={() => setFrage('pausieren')}><Pause className="w-3.5 h-3.5 mr-1" />Pausieren</Button>}
            {laeuft && z === 'pausiert' && <Button size="sm" variant="outline" disabled={busy} onClick={() => tu(() => ernaehrungAPI.aboFortsetzen(abo.id), 'Abo läuft wieder')}><Play className="w-3.5 h-3.5 mr-1" />Fortsetzen</Button>}
          </div>
        )}
        {neu && (
          <form className="space-y-2 rounded-md border p-2" onSubmit={(e) => { e.preventDefault(); if (!neu.einwilligung) { toast.error('Ohne Einwilligung in die Verarbeitung der Gesundheitsdaten kein Abo'); return; } tu(() => ernaehrungAPI.aboAnlegen({ lead_id: leadId, start: neu.start, preis_monat: Number(String(neu.preis_monat).replace(',', '.')), einwilligung_erteilt: true, eltern_name: neu.eltern_name?.trim() || null, ki: !!neu.ki, variante: neu.variante || 'standard' }), 'Zusatz-Abo angelegt — die App zeigt ab dem Start den Essen-Bereich'); }}>
            <div className="grid grid-cols-3 gap-2">
              {/* E22: Variante „Zyklus“ — eigener Preis aus den Produkt-Startwerten, nur ab 18; die Zyklus-Einwilligung gibt die Kundin in der App */}
              <FormField label="Variante">
                <select value={neu.variante || 'standard'} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" aria-label="Variante"
                  onChange={(e) => setNeu({ ...neu, variante: e.target.value, preis_monat: e.target.value === 'zyklus' ? (produkt?.preis_monat_zyklus ?? produkt?.preis_monat ?? 49) : (produkt?.preis_monat ?? 49) })}>
                  <option value="standard">Standard</option>
                  <option value="zyklus">Zyklus (ab 18)</option>
                </select>
              </FormField>
              <FormField label="Start"><Input type="date" required value={neu.start} onChange={(e) => setNeu({ ...neu, start: e.target.value })} /></FormField>
              <FormField label="Preis je Monat (€)"><Input inputMode="decimal" value={neu.preis_monat} onChange={(e) => setNeu({ ...neu, preis_monat: e.target.value })} /></FormField>
            </div>
            {neu.variante === 'zyklus' && <p className="text-xs text-muted-foreground">„Ernährungscoaching – Zyklus“: Phasen-Profile im Tagesziel, Symptom-Tracking, Zyklus-Karte unter Entwicklung. Die eigene Einwilligung zu den Zyklusdaten erteilt die Kundin in der App; ohne sie wird nichts erfasst.</p>}
            {/* E19: Einwilligung Art. 9 — Pflicht beim Verkauf; der Text steht unter Ernährung › Einstellungen (ENTWURF, bis freigegeben) */}
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={!!neu.einwilligung} onChange={(e) => setNeu({ ...neu, einwilligung: e.target.checked })} />
              <span>Die Person hat die <strong>Einwilligung in die Verarbeitung ihrer Gesundheitsdaten</strong> (Ernährung, Gewicht, Umfänge, Fotos) gelesen und unterschrieben. Unter 18 unterschreibt der Erziehungsberechtigte.</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Erziehungsberechtigter (nur unter 18)"><Input value={neu.eltern_name || ''} maxLength={120} onChange={(e) => setNeu({ ...neu, eltern_name: e.target.value })} placeholder="Name" /></FormField>
              <label className="flex items-start gap-2 text-sm pt-5">
                <input type="checkbox" className="mt-0.5" checked={!!neu.ki} onChange={(e) => setNeu({ ...neu, ki: e.target.checked })} />
                <span>KI-Unterstützung freiwillig erlaubt (Wochen-Zusammenfassung, Foto-Schätzung ohne Namen)</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Nur Leitung. Braucht eine laufende Mitgliedschaft (Beitragseinzug). Erster Monat anteilig ab dem Starttag. Die Einwilligung wird mit Fassung, Zeitpunkt und Kanal am Mitglied festgehalten; unter 18 werden die Kalorienzahlen in der App verborgen.</p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Anlegen</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setNeu(null)}>Abbrechen</Button>
            </div>
          </form>
        )}
        {frage === 'kuendigen' && <MitGrund text="Abo kündigen? Der Server rechnet den frühestmöglichen Termin." knopf="Kündigen" mitTermin mindestens={abo.mindestlaufzeit_bis} onAbbruch={() => setFrage(null)}
          onOk={(grund, zum) => tu(() => ernaehrungAPI.aboKuendigen(abo.id, { grund, zum }), 'Gekündigt')} />}
        {frage === 'pausieren' && <MitGrund text="Pausieren? Solange die Pause läuft, zeigt die App den Essen-Bereich nicht und es gibt keine Rechnungsposition." knopf="Pausieren" onAbbruch={() => setFrage(null)}
          onOk={(grund) => tu(() => ernaehrungAPI.aboPausieren(abo.id, grund), 'Pausiert')} />}
        <TagsZeile leadId={leadId} tags={stand?.tags} darf={darf} />
      </CardContent>
    </Card>
  );
}

/* Ernährung › Einstellungen: Startwerte des Abo-Produkts (Preis und Laufzeiten sind Konfiguration, nicht Code)
   und welche KI-Modelle in Coolify eingetragen sind (nur der Name, nie ein Schlüssel). */
export function AboProduktEinstellung() {
  const [p, setP] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const laden = () => ernaehrungAPI.aboProdukt().then((r) => { setP(r.data); setF({ preis_monat: String(r.data.preis_monat).replace('.', ','), preis_monat_zyklus: String(r.data.preis_monat_zyklus ?? r.data.preis_monat).replace('.', ','), mindestlaufzeit_monate: r.data.mindestlaufzeit_monate, kuendigungsfrist_monate: r.data.kuendigungsfrist_monate, startgebuehr: String(r.data.startgebuehr ?? 0).replace('.', ','), zahltag: String(r.data.zahltag ?? 1) }); }).catch(() => {});
  useEffect(() => { laden(); }, []);
  if (!p || !f) return null;
  const speichern = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await ernaehrungAPI.aboProduktSetzen({ preis_monat: Number(String(f.preis_monat).replace(',', '.')), preis_monat_zyklus: Number(String(f.preis_monat_zyklus).replace(',', '.')), mindestlaufzeit_monate: Number(f.mindestlaufzeit_monate), kuendigungsfrist_monate: Number(f.kuendigungsfrist_monate), startgebuehr: Number(String(f.startgebuehr).replace(',', '.')), zahltag: Number(f.zahltag) });
      setP({ ...p, ...data }); toast.success('Gespeichert — gilt für neue Abos');
    } catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };
  return (
    <Card className="mt-4" data-testid="abo-produkt">
      <CardHeader className="pb-2"><CardTitle className="text-base">Zusatz-Abo „Ernährungscoaching“</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">Position auf der bestehenden Monatsrechnung, Einzug mit dem vorhandenen SEPA-Mandat. Die Werte gelten für neue Abos; laufende behalten ihre.</p>
        <form onSubmit={speichern} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <FormField label="Preis je Monat (€ brutto)"><Input inputMode="decimal" value={f.preis_monat} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, preis_monat: e.target.value })} /></FormField>
          {/* E22: Variante „Zyklus“ — Startwert = Standardpreis, bis der Betreiber ihn setzt */}
          <FormField label="Preis Variante Zyklus (€)" hint="Startwert = Standardpreis"><Input inputMode="decimal" value={f.preis_monat_zyklus} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, preis_monat_zyklus: e.target.value })} /></FormField>
          <FormField label="Mindestlaufzeit (Monate)"><Input type="number" min="0" max="24" value={f.mindestlaufzeit_monate} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, mindestlaufzeit_monate: e.target.value })} /></FormField>
          <FormField label="Kündigungsfrist (Monate zum Monatsende)"><Input type="number" min="0" max="24" value={f.kuendigungsfrist_monate} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, kuendigungsfrist_monate: e.target.value })} /></FormField>
          {/* E23: Startgebühr (Startwert 0 €, je Abschluss änderbar) und Zahltag wie der Beitragseinzug */}
          <FormField label="Startgebühr (€, einmalig)" hint="0 = keine; je Abschluss änderbar"><Input inputMode="decimal" value={f.startgebuehr} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, startgebuehr: e.target.value })} /></FormField>
          <FormField label="Zahltag" hint="Einzugstag wie der Beitrag">
            <select value={f.zahltag} disabled={!p.darf_aendern} onChange={(e) => setF({ ...f, zahltag: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" aria-label="Zahltag">
              <option value="1">zum 1.</option><option value="15">zum 15.</option>
            </select>
          </FormField>
          {p.darf_aendern && <div className="sm:col-span-4"><Button type="submit" size="sm" disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button></div>}
        </form>
        {!p.darf_aendern && <p className="text-xs text-muted-foreground">Ändern kann das nur die Leitung.</p>}
        {p.ki && (
          <div className="rounded-md border p-2 text-xs">
            <p className="font-medium mb-1">KI-Modelle (nur über Umgebungsvariablen in Coolify)</p>
            <ul className="space-y-0.5">
              {Object.entries(p.ki).map(([zweck, k]) => (
                <li key={zweck}><code>{k.variable}</code>: {k.modell ? <span className="text-emerald-700">{k.modell}</span> : <span className="text-amber-700">nicht gesetzt — {zweck === 'foto' ? 'Essensfoto' : 'Coaching-Texte'} aus</span>}</li>
              ))}
            </ul>
            {/* E10 (15.10.2026): KI-Wochenauswertung — eigener Schalter KI_COACHING, die globale KI_FREIGABE bleibt aus */}
            {p.ki_woche && (
              <p className="mt-2" data-testid="ki-woche-stand">
                <span className="font-medium">KI-Wochenauswertung (Check-ins): </span>
                {p.ki_woche.verfuegbar
                  ? <span className="text-emerald-700">läuft — Entwürfe landen in der Freigabe-Zentrale, nichts geht automatisch raus</span>
                  : <span className="text-amber-700">aus — {p.ki_woche.grund}</span>}
                <span className="block text-muted-foreground mt-0.5">An das Modell gehen nur Wochen-Aggregate ohne Namen (Pseudonym); Datenschutzerklärung und AVV müssen die Verarbeitung nennen (E19).</span>
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* E19 Einwilligung & Datenschutz (15.10.2026): Einwilligungs-Log am Mitglied (Fassung, Zeitpunkt, Kanal, Eltern, KI),
   nachträglich eintragen (Leitung) und Widerruf — der Server pausiert das Abo und setzt die 30-Tage-Löschfrist. */
export function EinwilligungKarte({ leadId }) {
  const [s, setS] = useState(null);
  const [darf, setDarf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [frage, setFrage] = useState(null);
  const [eltern, setEltern] = useState('');
  const laden = () => ernaehrungAPI.einwilligung(leadId).then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { laden(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { ernaehrungAPI.aboProdukt().then((r) => setDarf(!!r.data.darf_aendern)).catch(() => setDarf(false)); }, []);
  if (!s) return null;
  const tu = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setS(data); setFrage(null); toast.success(ok); }
    catch (e) { toast.error(fehlertext(e)); }
    finally { setBusy(false); }
  };
  const KANAL = { app: 'in der App', crm_verkauf: 'im Studio (Verkauf)', crm: 'im CRM' };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Einwilligung Gesundheitsdaten
        <Badge variant="outline" className={s.erteilt ? 'bg-emerald-500/10 text-emerald-700 border-emerald-200' : 'bg-amber-500/10 text-amber-700 border-amber-200'}>{s.erteilt ? 'erteilt' : s.frueher ? 'frühere Fassung' : s.widerrufen_am ? 'widerrufen' : 'fehlt'}</Badge>
        {s.minderjaehrig && <Badge variant="outline">unter 18</Badge>}
      </CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <ul className="space-y-1">
          {s.erteilt && <li>Erteilt {KANAL[s.kanal] || s.kanal} am {tag(s.erteilt_am)} · Fassung {s.version}{s.entwurf ? ' (Entwurf)' : ''}{s.eltern ? ` · durch ${s.eltern}` : ''}</li>}
          {s.frueher && <li className="text-amber-700">Zugestimmt wurde einer früheren Fassung — in der App erneut bestätigen lassen.</li>}
          {s.widerrufen_am && !s.widerrufbar && <li>Widerrufen am {tag(s.widerrufen_am)}{s.loeschung_am ? ` · Ernährungsdaten werden am ${tag(s.loeschung_am)} gelöscht` : ''}</li>}
          <li>KI-Unterstützung: {s.ki.erteilt ? `erlaubt seit ${tag(s.ki.am)}` : 'nicht erlaubt'}</li>
          <li className="text-xs text-muted-foreground">{s.pflicht ? 'Die App sperrt den Coaching-Bereich ohne Einwilligung (Text ist freigegeben).' : 'Der Text ist noch nicht freigegeben — die App fragt, sperrt aber nicht (Ernährung › Einstellungen).'}</li>
        </ul>
        {darf && (
          <div className="flex flex-wrap gap-2">
            {!s.erteilt && !frage && <Button size="sm" variant="outline" onClick={() => setFrage('erteilen')}>Im Studio erteilt — eintragen</Button>}
            {s.widerrufbar && !frage && <Button size="sm" variant="ghost" onClick={() => setFrage('widerruf')}>Widerruf eintragen</Button>}
          </div>
        )}
        {frage === 'erteilen' && (
          <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
            <p>Die Person hat die aktuelle Fassung gelesen und unterschrieben{s.minderjaehrig ? ' — unter 18 der Erziehungsberechtigte:' : '.'}</p>
            {s.minderjaehrig && <Input value={eltern} onChange={(e) => setEltern(e.target.value)} placeholder="Name des Erziehungsberechtigten" maxLength={120} />}
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => tu(() => ernaehrungAPI.einwilligungErteilen(leadId, { version: s.version, eltern_name: eltern.trim() || null }), 'Einwilligung eingetragen')}>Eintragen</Button>
              <Button size="sm" variant="ghost" onClick={() => setFrage(null)}>Abbrechen</Button>
            </div>
          </div>
        )}
        {frage === 'widerruf' && <MitGrund text="Widerruf eintragen? Das Abo wird pausiert, die Ernährungsdaten werden nach 30 Tagen gelöscht. Abo und Nachweise bleiben." knopf="Widerrufen" onAbbruch={() => setFrage(null)}
          onOk={() => tu(() => ernaehrungAPI.einwilligungWiderrufen(leadId), 'Widerruf eingetragen — Abo pausiert')} />}
      </CardContent>
    </Card>
  );
}

/* Ernährung › Einstellungen: Text der Einwilligung lesen und als Leitung GENAU diese Fassung freigeben (Version +
   Prüfsumme im Server). Erst danach sperrt die App ohne Einwilligung. ENTWURF, bis ein Anwalt den Text geprüft hat. */
export function EinwilligungFreigabe() {
  const [s, setS] = useState(null);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ernaehrungAPI.einwilligungEinstellung().then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const setzen = async (freigeben) => {
    setBusy(true);
    try { const { data } = await ernaehrungAPI.einwilligungFreigeben({ freigeben, version: s.version }); setS(data); toast.success(freigeben ? 'Fassung freigegeben — die App sperrt jetzt ohne Einwilligung' : 'Freigabe zurückgenommen'); }
    catch (e) { toast.error(fehlertext(e)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="mt-4">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Einwilligung Ernährungscoaching
        <Badge variant="outline">{s.version}</Badge>
        {s.entwurf && <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-200">ENTWURF</Badge>}
        {s.freigegeben && <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-200">freigegeben</Badge>}
      </CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">Gesundheitsdaten (Art. 9 DSGVO): Diesen Text liest die Person in der App beim ersten Öffnen und im Studio beim Zusatz-Abo. {s.freigegeben ? `Freigegeben von ${s.freigegeben.von} am ${tag(s.freigegeben.am)} — ohne Einwilligung sperrt die App.` : 'Noch nicht freigegeben: Die App fragt, sperrt aber nicht. Vor dem ersten Kunden anwaltlich prüfen lassen, dann hier freigeben.'} Widerruf: Abo pausiert, Löschung nach {s.loeschfrist_tage} Tagen.</p>
        <Button size="sm" variant="outline" onClick={() => setOffen(!offen)}>{offen ? 'Text einklappen' : 'Text lesen'}</Button>
        {offen && (
          <div className="space-y-2">
            <p className="font-medium">{s.titel}</p>
            <pre className="whitespace-pre-wrap text-xs rounded-md border p-2 bg-muted/40 font-sans">{s.text}</pre>
            <p className="font-medium">{s.ki.titel}</p>
            <pre className="whitespace-pre-wrap text-xs rounded-md border p-2 bg-muted/40 font-sans">{s.ki.text}</pre>
          </div>
        )}
        {s.darf_freigeben && (
          <div className="flex gap-2">
            {!s.freigegeben && <Button size="sm" disabled={busy || !offen} onClick={() => setzen(true)}>Diese Fassung freigeben</Button>}
            {s.freigegeben && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setzen(false)}>Freigabe zurücknehmen</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
